import { useEffect, useMemo } from 'react';

import Script from 'next/script';

import { IMPACT_CLICK_ID } from '@editor/global/constants';
import {
  useAuthenticationStore,
  authenticationStoreSelector,
} from '@editor/stores/authenticationStore';
import { getAdClickId } from '@editor/utils/adClickId';
import { readUtmData } from '@editor/utils/utm';
import { USER_API_URL } from '@repo/editor/src/api/constants';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import {
  getProfileToken,
  PROFILE_ACCESS_TOKEN_KEY,
} from '@repo/transport/token';
import { getDeviceScreenSizeType } from '@repo/ui/queries';
import { buildSignupUrl } from '@repo/utils/url';
import Cookies from 'js-cookie';

import { GoogleOneTapContainer } from './styles';

const oneTapScriptId = 'g_id_onload';
const oneTapScriptUrl = 'https://accounts.google.com/gsi/client';

const OneTapLogin = () => {
  const user = useUserStore(userStoreSelector.loadingUser);
  const authOptions = useAuthenticationStore(
    authenticationStoreSelector.options
  );
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

  // Unmount the google one tap div element when the user is logged in
  // This is to prevent the one tap prompt from being shown again after the user has logged in
  useEffect(() => {
    if (user !== null && getProfileToken()) {
      document.getElementById(oneTapScriptId)?.remove();
    }
  }, [user]);

  const params = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    const loginUri = `${USER_API_URL}/sessions/onetap/callback`;
    const deviceScreenSizeType = getDeviceScreenSizeType();
    const impactClickId = Cookies.get(IMPACT_CLICK_ID);
    const adClickId = getAdClickId();
    const utm = readUtmData();
    const signupUrl = buildSignupUrl();

    return {
      // Google One Tap attributes
      // See: https://developers.google.com/identity/gsi/web/reference/html-reference#element_with_id_g_id_onload
      id: oneTapScriptId,
      'data-login_uri': loginUri,
      'data-client_id': googleClientId,
      'data-prompt_parent_id': oneTapScriptId,
      'data-cancel_on_tap_outside': 'false',
      // Stop the one tap prompt script when we have a user access token
      // See: https://developers.google.com/identity/gsi/web/guides/toggle-display-with-cookies
      'data-skip_prompt_cookie': PROFILE_ACCESS_TOKEN_KEY,

      // Custom attributes for analytics and user attribution
      // These are passed to the backend during authentication
      'data-ad_click_id': adClickId,
      'data-device_screen_size_type': deviceScreenSizeType,
      'data-impact_click_id': impactClickId,
      'data-invite_id': authOptions.inviteCode,
      'data-signup_url': signupUrl,
      'data-team_invite_id': authOptions.teamInviteCode,
      'data-utm': utm,
    };
  }, [googleClientId, authOptions]);

  // If user is logged in, or if googleClientId is not set,
  // or if window is not available, return null
  if (user !== null || !googleClientId || typeof window === 'undefined') {
    return null;
  }

  return (
    <GoogleOneTapContainer>
      <Script async defer src={oneTapScriptUrl} />
      <div {...params} />
    </GoogleOneTapContainer>
  );
};

export default withEventScope(OneTapLogin, { module: 'oneTapLogin' });
